import { Box, Divider, IconButton, Typography } from '@mui/material';
import { EmployeeResponse } from 'api/services/workforce/workforce-service.types';
import { IconCall, IconCross, IconInbox } from 'assets/img';
import { DateTime } from 'luxon';
import { accreditationsService } from 'ogp/services';
import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { LwButton, LwFormDate, LWModal, UserAvatar } from 'redesign';
import { useAlert } from 'shared/components/alerts';
import { useTranslation } from 'react-i18next';
import {
  ButtonBigWrap,
  ButtonWrap,
  ButtonWrapCertificate,
  CertificateInfo,
  CertificateInfoWrapper,
  CertificateName,
  CertificateWrap,
  CredentialText,
  DateBox,
  FlexColumnBoxWrap,
  FlexRowBoxCenter,
  HeaderWrapper,
  ImageWrap,
  InfoWrap,
  ModalWrapperBox,
  NameBox,
  OpenCertificateButton,
  StyledIcon,
  StyledIconDocument,
} from 'shared/components/common/certificate-modal-styled-components';
import { useUpdateCertificateValidUntil } from '../hooks/use-update-certificate-valid-until';

type CertificateData = {
  accreditationId: string;
  certificateName: string;
  certificateType: string;
  hasFile: boolean;
  currentEndDate: string | null;
};

type WorkerData = {
  flexWorkerId: string;
  employeeData?: EmployeeResponse;
};

type EditCertificateEndDateModalProps = {
  isOpen: boolean;
  onClose: () => void;
  certificate: CertificateData;
  worker: WorkerData;
};

type FormData = {
  validUntil: string | null;
};

export const EditCertificateEndDateModal = ({
  isOpen,
  onClose,
  certificate,
  worker,
}: EditCertificateEndDateModalProps) => {
  const { alertSuccess, alertError } = useAlert();
  const { t } = useTranslation(['web-common', 'ogp-screen-specific']);
  const mutation = useUpdateCertificateValidUntil(worker.flexWorkerId);
  const [fileUrl, setFileUrl] = useState<string | null>(null);
  const [isLoadingFile, setIsLoadingFile] = useState(false);

  const formMethods = useForm<FormData>({
    defaultValues: {
      validUntil: certificate.currentEndDate,
    },
  });

  const handleOpenAccreditation = async () => {
    if (!certificate.hasFile) {
      return;
    }

    if (!fileUrl) {
      setIsLoadingFile(true);
      try {
        const response = await accreditationsService.getCertificateFileUrl(
          worker.flexWorkerId,
          certificate.accreditationId
        );
        if (response.url) {
          setFileUrl(response.url);
          window.open(response.url, '_blank');
        }
      } catch (e) {
        alertError(e);
      } finally {
        setIsLoadingFile(false);
      }
    } else {
      window.open(fileUrl, '_blank');
    }
  };

  const handleSubmit = formMethods.handleSubmit((data) => {
    const validUntil = data.validUntil ? DateTime.fromISO(data.validUntil) : null;

    mutation.mutate(
      { accreditationId: certificate.accreditationId, validUntil },
      {
        onSuccess: () => {
          alertSuccess(
            t('edit-certificate-end-date-modal.success-message', { ns: 'ogp-screen-specific' })
          );
          onClose();
        },
        onError: (error) => {
          alertError(error);
        },
      }
    );
  });

  const handleCancel = () => {
    formMethods.reset();
    onClose();
  };

  return (
    <LWModal isOpen={isOpen} onClose={handleCancel} testId="edit-certificate-end-date-modal">
      <ModalWrapperBox>
        {worker.employeeData && (
          <Box>
            <HeaderWrapper>
              <FlexRowBoxCenter role="fw-profile">
                <ImageWrap>
                  <UserAvatar
                    firstName={worker.employeeData.firstName ?? ''}
                    lastName={worker.employeeData.lastName ?? ''}
                    imageUrl={worker.employeeData.profilePictureUrl}
                    width={14}
                    height={14}
                  />
                </ImageWrap>
                <Typography variant="h3" fontWeight={500} noWrap>
                  {worker.employeeData.firstName} {worker.employeeData.lastName}
                </Typography>
              </FlexRowBoxCenter>
              <InfoWrap>
                <Box display="flex" flexDirection="row">
                  <StyledIcon width={24} height={24} as={IconCall} />
                  <CredentialText paddingRight={5} noWrap>
                    {worker.employeeData.phone ?? ''}
                  </CredentialText>
                </Box>
                <Box display="flex" flexDirection="row">
                  <StyledIcon width={24} height={24} as={IconInbox} />
                  <CredentialText noWrap>{worker.employeeData.email}</CredentialText>
                </Box>
                <Box display="flex" flexDirection="row">
                  <IconButton onClick={handleCancel}>
                    <IconCross width={24} height={24} />
                  </IconButton>
                </Box>
              </InfoWrap>
            </HeaderWrapper>
            <Divider />
          </Box>
        )}
        <FormProvider {...formMethods}>
          <FlexColumnBoxWrap>
            <CertificateInfo>
              <CertificateWrap>
                <FlexRowBoxCenter>
                  <StyledIconDocument
                    width={130}
                    height={150}
                    onClick={handleOpenAccreditation}
                    hasFile={certificate.hasFile}
                  />
                </FlexRowBoxCenter>
                <ButtonWrapCertificate>
                  <OpenCertificateButton
                    onClick={handleOpenAccreditation}
                    role="button"
                    disabled={!certificate.hasFile || isLoadingFile}
                  >
                    {t('edit-certificate-end-date-modal.open-certificate', {
                      ns: 'ogp-screen-specific',
                    })}
                  </OpenCertificateButton>
                </ButtonWrapCertificate>
              </CertificateWrap>
              <CertificateInfoWrapper>
                <NameBox>
                  <CertificateName>
                    {t('edit-certificate-end-date-modal.certificate-label', {
                      ns: 'ogp-screen-specific',
                    })}
                    : {certificate.certificateName}
                  </CertificateName>
                </NameBox>
                <NameBox>
                  <CertificateName>
                    {t('edit-certificate-end-date-modal.certificate-type-label', {
                      ns: 'ogp-screen-specific',
                    })}
                    : {certificate.certificateType}
                  </CertificateName>
                </NameBox>
                <DateBox>
                  <LwFormDate
                    name="validUntil"
                    label={t('edit-certificate-end-date-modal.set-expiration-date-label', {
                      ns: 'ogp-screen-specific',
                    })}
                    control={formMethods.control}
                    disabled={false}
                  />
                </DateBox>
              </CertificateInfoWrapper>
            </CertificateInfo>
            <ButtonBigWrap>
              <ButtonWrap>
                <LwButton color="secondary" onClick={handleCancel}>
                  {t('buttons.cancel', { ns: 'web-common' })}
                </LwButton>
                <LwButton color="primary" onClick={handleSubmit} disabled={mutation.isLoading}>
                  {t('buttons.save', { ns: 'web-common' })}
                </LwButton>
              </ButtonWrap>
            </ButtonBigWrap>
          </FlexColumnBoxWrap>
        </FormProvider>
      </ModalWrapperBox>
    </LWModal>
  );
};
